/**
 * Project Name: Brainsie eCommerce v4
 * Component: Page Renderer
 * Description: Dynamic page renderer that loads and displays CMS-managed pages
 * Author: Brainsie Team
 * Date Created: October 24, 2025
 */

import React, { useState, useEffect, useContext } from "react";
import { Box, Alert, Skeleton, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ThemeContext } from "context/ThemeContext";
import SectionRenderer from "./SectionRenderer";
import PageBannerRenderer from "./components/BannerComponents/PageBannerRenderer";
import { usePageContent } from "./hooks/usePageContent";
import { ProductsProvider } from "context/ProductsProvider";

export default function PageRenderer({
  pageId,
  headerRefresh,
  setHeaderRefresh,
}) {
  const {
    pageData,
    sections,
    banners,
    allContent,
    loading,
    error,
    fetchPageContent,
  } = usePageContent();
  const theme = useTheme();
  const themeMode = useContext(ThemeContext);
  const darkMode = themeMode.state.darkMode;

  useEffect(() => {
    if (pageId) {
      fetchPageContent(pageId);
    }
  }, [pageId, fetchPageContent]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: darkMode
            ? theme.palette.grey[900]
            : theme.palette.background.default,
        }}
      >
        {/* Hero Banner Skeleton */}
        <Box sx={{ mb: 6 }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            // height={{ xs: 450, sm: 550, md: "70vh" }}
            sx={{
              borderRadius: "12px",
              height: { xs: 450, sm: 550, md: "70vh" },
              backgroundColor: darkMode
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)",
            }}
          />
        </Box>

        {/* Content Sections Skeleton */}
        <Container maxWidth="lg" sx={{ mb: 6 }}>
          {[1, 2, 3].map((item) => (
            <Box key={item} sx={{ mb: 4 }}>
              <Skeleton
                variant="text"
                width="40%"
                height={60}
                sx={{
                  mb: 2,
                  backgroundColor: darkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={200}
                sx={{
                  mb: 2,
                  borderRadius: "8px",
                  backgroundColor: darkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                }}
              />
              <Skeleton
                variant="text"
                width="80%"
                height={40}
                sx={{
                  backgroundColor: darkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                }}
              />
            </Box>
          ))}
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 3,
          backgroundColor: darkMode
            ? theme.palette.grey[900]
            : theme.palette.background.default,
        }}
      >
        <Alert
          severity="error"
          sx={{
            backgroundColor: darkMode
              ? theme.palette.error.dark
              : theme.palette.error.light,
            color: darkMode
              ? theme.palette.common.white
              : theme.palette.text.primary,
          }}
        >
          Error loading page: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!pageData) {
    return (
      <Box
        sx={{
          p: 3,
          backgroundColor: darkMode
            ? theme.palette.grey[900]
            : theme.palette.background.default,
        }}
      >
        <Alert
          severity="warning"
          sx={{
            backgroundColor: darkMode
              ? theme.palette.warning.dark
              : theme.palette.warning.light,
            color: darkMode
              ? theme.palette.common.white
              : theme.palette.text.primary,
          }}
        >
          Page not found
        </Alert>
      </Box>
    );
  }

  // Check if page has product-related dynamic sections that need ProductsProvider
  const hasProductSections = allContent?.some(
    (content) =>
      content.contentType === "section" &&
      // Check by Section_Master_Id (6=Featured Products, 7=Best Sellers, 8=Popular Products)
      ((content.Section_Master_Id &&
        [6, 7, 8].includes(content.Section_Master_Id)) ||
        // Fallback: Check by Section_Master_Code
        (content.Section_Master_Code &&
          ["SEC_006", "SEC_007", "SEC_008"].includes(
            content.Section_Master_Code
          )))
  );

  const renderPageContent = () => (
    <Box
      className="page-renderer"
      sx={{
        minHeight: "100vh",
      }}
    >
      {/* Page Meta Information */}
      {pageData.Page_Name && <title>{pageData.Page_Name}</title>}

      {/* Render All Content (Sections and Banners) in Display Order */}
      {allContent && allContent.length > 0 ? (
        allContent.map((content, index) => {
          if (content.contentType === "banner") {
            // Group banners by Banner_Code at this Display_Order
            const bannersAtThisOrder = allContent.filter(
              (item) =>
                item.contentType === "banner" &&
                item.Display_Order === content.Display_Order &&
                item.Banner_Code === content.Banner_Code
            );

            // Only render once per Banner_Code group
            const isFirstInGroup =
              bannersAtThisOrder.findIndex(
                (b) => b.Banner_ID === content.Banner_ID
              ) === 0;

            if (isFirstInGroup) {
              return (
                <PageBannerRenderer
                  key={`banner_${content.Banner_Code}_${content.Display_Order}`}
                  banners={bannersAtThisOrder}
                />
              );
            }
            return null;
          } else if (content.contentType === "section") {
            return (
              <SectionRenderer
                key={`section_${content.Section_Id}`}
                section={content}
                pageData={pageData}
                darkMode={darkMode}
                theme={theme}
                headerRefresh={headerRefresh}
                setHeaderRefresh={setHeaderRefresh}
              />
            );
          }
          return null;
        })
      ) : (
        <Box
          sx={{
            p: 3,
            backgroundColor: darkMode
              ? theme.palette.grey[800]
              : theme.palette.background.paper,
          }}
        >
          <Alert
            severity="info"
            sx={{
              backgroundColor: darkMode
                ? theme.palette.info.dark
                : theme.palette.info.light,
              color: darkMode
                ? theme.palette.common.white
                : theme.palette.text.primary,
            }}
          >
            No content found for this page
          </Alert>
        </Box>
      )}
    </Box>
  );

  // Wrap with ProductsProvider if needed
  return hasProductSections ? (
    <ProductsProvider>{renderPageContent()}</ProductsProvider>
  ) : (
    renderPageContent()
  );
}
